/**
 * CADMarkerDetailPanel — side drawer showing DRBFM failure-mode details for
 * a clicked CAD marker. The marker's `detailPayload` carries a `FailureMode`
 * when the marker came from `useDRBFMWithCAD`.
 *
 * Tied to the Toyota Gosei DRBFM↔3D CAD linker PoC (TG #673).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import type { CADMarker } from '../../types/cad';
import { getSeverityColor } from '../../types/drbfm';
import type { FailureMode } from '../../types/drbfm';

interface Props {
  open: boolean;
  marker: CADMarker | null;
  onClose: () => void;
}

export const CADMarkerDetailPanel: React.FC<Props> = ({ open, marker, onClose }) => {
  const { t } = useTranslation();
  const groupedMarkers = marker?.groupedMarkers?.length
    ? marker.groupedMarkers
    : marker
      ? [marker]
      : [];
  const failureModeMarkers = groupedMarkers
    .map((groupedMarker) => ({
      marker: groupedMarker,
      failureMode: groupedMarker.detailPayload as FailureMode | undefined,
    }))
    .filter(
      (
        entry,
      ): entry is { marker: CADMarker; failureMode: FailureMode } =>
        Boolean(entry.failureMode),
    );

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-[380px] max-w-none overflow-y-auto sm:max-w-none"
      >
        <SheetTitle className="mb-2">{t('cad.markerDetail.title')}</SheetTitle>

        {failureModeMarkers.length === 0 && (
          <Alert variant="info">
            <AlertDescription>{t('cad.markerDetail.noData')}</AlertDescription>
          </Alert>
        )}

        {failureModeMarkers.length > 0 && (
          <div className="flex flex-col gap-4">
            {failureModeMarkers.length > 1 && (
              <Alert variant="info">
                <AlertDescription>
                  {t('cad.markerDetail.groupedCount', {
                    count: failureModeMarkers.length,
                  })}
                </AlertDescription>
              </Alert>
            )}
            {failureModeMarkers.map(({ marker: groupedMarker, failureMode }, index) => (
              <React.Fragment key={failureMode.id}>
                {index > 0 && <Separator />}
                <FailureModeDetails
                  marker={groupedMarker}
                  failureMode={failureMode}
                />
              </React.Fragment>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const FailureModeDetails: React.FC<{
  marker: CADMarker;
  failureMode: FailureMode;
}> = ({ marker, failureMode: fm }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row flex-wrap gap-2">
        <Badge
          className="border-transparent text-white"
          style={{ backgroundColor: getSeverityColor(fm.severity) }}
        >
          {fm.severity}
        </Badge>
        {typeof fm.rpnScore === 'number' && (
          <Badge variant="outline">{`RPN ${fm.rpnScore}`}</Badge>
        )}
        {marker.confidence !== undefined && (
          <Badge variant={marker.confidence < 0.4 ? 'outline' : 'default'}>
            {t('cad.markerDetail.confidence', {
              value: (marker.confidence * 100).toFixed(0),
            })}
          </Badge>
        )}
        {fm.cadAnchor?.source && (
          <Badge variant="outline">
            {t(`cad.marker.source.${fm.cadAnchor.source}`)}
          </Badge>
        )}
      </div>

      <p className="text-base font-medium text-foreground">{fm.failureDescription}</p>

      <Section title={t('cad.markerDetail.cause')}>{fm.failureCause || '—'}</Section>
      <Section title={t('cad.markerDetail.effect')}>{fm.failureEffect || '—'}</Section>

      {fm.cadAnchor?.partLabel && (
        <Section title={t('cad.markerDetail.part')}>{fm.cadAnchor.partLabel}</Section>
      )}

      {fm.cadAnchor?.reasoning && (
        <Section title={t('cad.markerDetail.locationEvidence')}>{fm.cadAnchor.reasoning}</Section>
      )}

      {fm.knowledgeReferences?.length > 0 && (
        <>
          <p className="text-sm font-medium text-foreground">
            {t('cad.markerDetail.relatedDocuments')}
          </p>
          <div className="flex flex-col gap-1">
            {fm.knowledgeReferences.map((k) => (
              <a
                key={k.documentId}
                href={`/documents/${k.documentId}`}
                className="text-sm text-primary hover:underline"
              >
                {k.title}
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div>
    <span className="text-xs text-muted-foreground">{title}</span>
    <p className="text-sm text-foreground">{children}</p>
  </div>
);
